import { FiltersConfig } from "#routes/data-tools/submissions/datasets/DataSetSubmissionsList.helpers";
import { FilterArea } from "#src/components/FilterArea";
import { useListDatasets } from "#src/components/hooks/adapters/useDatasets";
import {
  DateSelectorInput,
  DropdownInput,
  type StorageKeys,
} from "@validereinc/common-components";
import { TransactionSources, TransactionStatus } from "@validereinc/domain";
import { Transactions } from "@validereinc/domain-controllers";
import { endOfDay, subDays } from "date-fns";
import React from "react";
import { z } from "zod";

const { TransactionSourceOptions, TransactionStatusOptions } =
  Transactions.view;

const defaultRetentionWindowInDays = 7;
const defaultDateRange = {
  from: subDays(new Date(), defaultRetentionWindowInDays),
  to: endOfDay(new Date()),
};

export const DataSubmissionFilterAreaSchema = z
  .object({
    dataset: z.string(),
    source: z.nativeEnum(TransactionSources),
    dateRange: z.object({
      from: z.string().datetime(),
      to: z.string().datetime().optional(),
    }),
    status: z.nativeEnum(TransactionStatus),
  })
  .partial();
export type DataSubmissionFilterAreaType = z.infer<
  typeof DataSubmissionFilterAreaSchema
>;

export const DataSubmissionsFilterArea = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const { data: allDatasets, isLoading: allDatasetsIsLoading } =
    useListDatasets();

  return (
    <FilterArea.Root<{
      source: string;
      dateRange: { from: Date; to: Date };
    }>
      storageKey={viewConfigStorageKey}
      defaultValues={{
        [FiltersConfig.source.name]: "upload",
        [FiltersConfig.date.name]: defaultDateRange,
      }}
    >
      <FilterArea.Container>
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <DropdownInput
                {...FiltersConfig.dataset}
                options={
                  allDatasets?.data?.map((set) => ({
                    name: set.name,
                    id: set.id,
                  })) ?? []
                }
                isLoading={allDatasetsIsLoading}
                labelKey="name"
                valueKey="id"
                isLabelShown={false}
                isInline
                onChange={(val) => handleOnChange(val, "dataset")}
              />
              <DropdownInput
                {...FiltersConfig.source}
                options={TransactionSourceOptions}
                labelKey="label"
                valueKey="value"
                isLabelShown={false}
                isInline
                onChange={(val) => handleOnChange(val, "source")}
              />
              <DateSelectorInput
                {...FiltersConfig.date}
                variant="time"
                isRange
                isInline
                isLabelShown={false}
                max={defaultDateRange.to}
                onChange={(val) => handleOnChange(val, "dateRange")}
              />
              <DropdownInput
                {...FiltersConfig.status}
                options={TransactionStatusOptions}
                labelKey="label"
                valueKey="value"
                isInline
                isFluid
                onChange={(val) => handleOnChange(val, "status")}
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
